import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import { useGetAgriculturalInfluencerQuery, useCreateAgriculturalInfluencerMutation, useUpdateAgriculturalInfluencerMutation, useGetCountriesQuery, useGetCitiesQuery } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import ImageUpload from '../../../components/ImageUpload';
import { showSuccess, showError } from '../../../utils/toast';
import { useLanguage } from '../../../context/LanguageContext';

const AdminAgriculturalInfluencerForm: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  
  const { data, isLoading } = useGetAgriculturalInfluencerQuery(id!, { skip: !isEdit });
  const [createInfluencer, { isLoading: isCreating }] = useCreateAgriculturalInfluencerMutation();
  const [updateInfluencer, { isLoading: isUpdating }] = useUpdateAgriculturalInfluencerMutation();

  const [formData, setFormData] = useState({
    title: '',
    title_ar: '',
    country_id: null as number | null,
    city_id: null as number | null,
    role: '',
    role_ar: '',
    location: '',
    location_ar: '',
    email: '',
    phone: '',
    about: '',
    about_ar: '',
    instagram: '',
    snapchat: '',
    tiktok: '',
    youtube: '',
    facebook: '',
    threads: '',
    icon: null as File | null,
    cover_image: null as File | null,
  });

  const { data: countriesData } = useGetCountriesQuery({ per_page: 1000 });
  const { data: citiesData } = useGetCitiesQuery(
    { per_page: 2000, country_id: formData.country_id },
    { skip: !formData.country_id }
  );

  const countryOptions = (countriesData?.data?.data || countriesData?.data || [])
    .map((c: any) => ({ value: c.id, label: c.name || c.name_ar }));
  const cityOptions = (citiesData?.data?.data || citiesData?.data || [])
    .map((c: any) => ({ value: c.id, label: c.name || c.name_ar }));

  const [existingIcon, setExistingIcon] = useState<string | undefined>(undefined);
  const [existingCover, setExistingCover] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (isEdit && data?.data) {
      const influencer = data.data;
      setFormData({
        title: influencer.title || '',
        title_ar: influencer.title_ar || '',
        country_id: influencer.country_id || null,
        city_id: influencer.city_id || null,
        role: influencer.role || '',
        role_ar: influencer.role_ar || '',
        location: influencer.location || '',
        location_ar: influencer.location_ar || '',
        email: influencer.email || '',
        phone: influencer.phone || '',
        about: influencer.about || '',
        about_ar: influencer.about_ar || '',
        instagram: influencer.instagram || '',
        snapchat: influencer.snapchat || '',
        tiktok: influencer.tiktok || '',
        youtube: influencer.youtube || '',
        facebook: influencer.facebook || '',
        threads: influencer.threads || '',
        icon: null,
        cover_image: null,
      });
      
      if (influencer.icon) {
        const iconFilename = influencer.icon.includes('/uploads/') 
          ? influencer.icon.split('/uploads/')[1] 
          : influencer.icon;
        setExistingIcon(iconFilename);
      } else {
        setExistingIcon(undefined);
      }

      if (influencer.cover_image) {
        const coverFilename = influencer.cover_image.includes('/uploads/') 
          ? influencer.cover_image.split('/uploads/')[1] 
          : influencer.cover_image;
        setExistingCover(coverFilename);
      } else {
        setExistingCover(undefined);
      }
    }
  }, [isEdit, data]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleIconChange = (file: File | null) => {
    setFormData(prev => ({ ...prev, icon: file }));
  };

  const handleCoverChange = (file: File | null) => {
    setFormData(prev => ({ ...prev, cover_image: file }));
  };

  const handleRemoveExistingIcon = () => {
    setExistingIcon(undefined);
  };

  const handleRemoveExistingCover = () => {
    setExistingCover(undefined);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.title.trim()) {
      showError(t('Name is required'));
      return;
    }

    if (!formData.title_ar.trim()) {
      showError(t('Name (Arabic) is required'));
      return;
    }
    
    try {
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('title_ar', formData.title_ar);
      // Always sent, so clearing a select is saved as null
      submitData.append('country_id', formData.country_id ? String(formData.country_id) : '');
      submitData.append('city_id', formData.city_id ? String(formData.city_id) : '');
      if (formData.role) submitData.append('role', formData.role);
      if (formData.role_ar) submitData.append('role_ar', formData.role_ar);
      if (formData.location) submitData.append('location', formData.location);
      if (formData.location_ar) submitData.append('location_ar', formData.location_ar);
      if (formData.email) submitData.append('email', formData.email);
      if (formData.phone) submitData.append('phone', formData.phone);
      if (formData.about) submitData.append('about', formData.about);
      if (formData.about_ar) submitData.append('about_ar', formData.about_ar);
      if (formData.instagram) submitData.append('instagram', formData.instagram);
      if (formData.snapchat) submitData.append('snapchat', formData.snapchat);
      if (formData.tiktok) submitData.append('tiktok', formData.tiktok);
      if (formData.youtube) submitData.append('youtube', formData.youtube);
      if (formData.facebook) submitData.append('facebook', formData.facebook);
      if (formData.threads) submitData.append('threads', formData.threads);
      
      if (formData.icon) {
        submitData.append('icon', formData.icon);
      }

      if (formData.cover_image) {
        submitData.append('cover_image', formData.cover_image);
      }

      if (isEdit) {
        const result = await updateInfluencer({ id: parseInt(id!), data: submitData }).unwrap();
        showSuccess(result.message || t('Influencer updated successfully!'));
      } else {
        const result = await createInfluencer(submitData).unwrap();
        showSuccess(result.message || t('Influencer created successfully!'));
      }

      navigate('/admin/agricultural-influencers');
    } catch (err: any) {
      if (err?.data?.errors) {
        const errorMessages = Object.values(err.data.errors).flat().join(', ');
        showError(errorMessages || err?.data?.message || t('Validation failed'));
      } else {
        showError(err?.data?.message || (isEdit ? t('Failed to update influencer. Please try again.') : t('Failed to create influencer. Please try again.')));
      }
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {isEdit ? t('Edit Influencer') : t('Add New Influencer')}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }} className="text-muted text-hover-primary">{t('Dashboard')}</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/agricultural-influencers'); }} className="text-muted text-hover-primary">{t('Agricultural Influencers')}</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-body pt-0">
                <form onSubmit={handleSubmit} className="form fv-plugins-bootstrap5 fv-plugins-framework">
                  <div className="row g-9 my-3">
                    {/* Basic Info */}
                    <div className="col-md-6 fv-row">
                      <label className="required fs-6 fw-semibold mb-2">{t('Name')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('Enter Name')}
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="col-md-6 fv-row">
                      <label className="required fs-6 fw-semibold mb-2">{t('Name (Arabic)')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('Enter Name in Arabic')}
                        name="title_ar"
                        value={formData.title_ar}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">{t('Country')}</label>
                      <Select
                        isClearable
                        isSearchable
                        options={countryOptions}
                        value={countryOptions.find((o: any) => o.value === formData.country_id) || null}
                        onChange={(opt) => setFormData(prev => ({ ...prev, country_id: (opt as any)?.value ?? null, city_id: null }))}
                        placeholder={t('Select Country')}
                        className="react-select-container"
                        classNamePrefix="react-select"
                      />
                    </div>

                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">{t('City')}</label>
                      <Select
                        isClearable
                        isSearchable
                        isDisabled={!formData.country_id}
                        options={cityOptions}
                        value={cityOptions.find((o: any) => o.value === formData.city_id) || null}
                        onChange={(opt) => setFormData(prev => ({ ...prev, city_id: (opt as any)?.value ?? null }))}
                        placeholder={formData.country_id ? t('Select City') : t('Select a country first')}
                        className="react-select-container"
                        classNamePrefix="react-select"
                      />
                    </div>

                    <div className="col-md-6 fv-row">
                      <label className="fs-6 fw-semibold mb-2">{t('Role')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('e.g. Farmer, Expert')}
                        name="role"
                        value={formData.role}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="col-md-6 fv-row">
                      <label className="fs-6 fw-semibold mb-2">{t('Role (Arabic)')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('e.g. مزارع، خبير')}
                        name="role_ar"
                        value={formData.role_ar}
                        onChange={handleInputChange}
                        dir="rtl"
                      />
                    </div>

                    <div className="col-md-6 fv-row">
                      <label className="fs-6 fw-semibold mb-2">{t('Location')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('Enter Location')}
                        name="location"
                        value={formData.location}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="col-md-6 fv-row">
                      <label className="fs-6 fw-semibold mb-2">{t('Location (Arabic)')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('أدخل الموقع')}
                        name="location_ar"
                        value={formData.location_ar}
                        onChange={handleInputChange}
                        dir="rtl"
                      />
                    </div>

                    <div className="col-md-6 fv-row">
                      <label className="fs-6 fw-semibold mb-2">{t('Email')}</label>
                      <input
                        type="email"
                        className="form-control form-control-solid"
                        placeholder={t('Enter Email')}
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="col-md-6 fv-row">
                      <label className="fs-6 fw-semibold mb-2">{t('Phone')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('Enter Phone')}
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="col-md-6 fv-row">
                      <label className="fs-6 fw-semibold mb-2">{t('About')}</label>
                      <textarea
                        className="form-control form-control-solid"
                        rows={3}
                        placeholder={t('About the influencer...')}
                        name="about"
                        value={formData.about}
                        onChange={handleInputChange}
                      ></textarea>
                    </div>

                    <div className="col-md-6 fv-row">
                      <label className="fs-6 fw-semibold mb-2">{t('About (Arabic)')}</label>
                      <textarea
                        className="form-control form-control-solid"
                        rows={3}
                        placeholder={t('نبذة عن المؤثر...')}
                        name="about_ar"
                        value={formData.about_ar}
                        onChange={handleInputChange}
                        dir="rtl"
                      ></textarea>
                    </div>

                    {/* Images */}
                    <div className="col-md-6 fv-row">
                      <ImageUpload
                        existingImage={existingIcon}
                        onImageChange={handleIconChange}
                        onRemoveExisting={handleRemoveExistingIcon}
                        label={t('Profile Icon')}
                        name="icon"
                      />
                    </div>

                    <div className="col-md-6 fv-row">
                      <ImageUpload
                        existingImage={existingCover}
                        onImageChange={handleCoverChange}
                        onRemoveExisting={handleRemoveExistingCover}
                        label={t('Cover Image')}
                        name="cover_image"
                      />
                    </div>

                    {/* Social Media */}
                    <div className="col-12">
                      <h3 className="fw-bold fs-4 mb-3">{t('Social Media Links')}</h3>
                    </div>

                    <div className="col-md-4 fv-row">
                      <label className="fs-6 fw-semibold mb-2">{t('Instagram')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('Instagram URL')}
                        name="instagram"
                        value={formData.instagram}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="col-md-4 fv-row">
                      <label className="fs-6 fw-semibold mb-2">{t('Snapchat')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('Snapchat URL')}
                        name="snapchat"
                        value={formData.snapchat}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="col-md-4 fv-row">
                      <label className="fs-6 fw-semibold mb-2">{t('TikTok')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('TikTok URL')}
                        name="tiktok"
                        value={formData.tiktok}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="col-md-4 fv-row">
                      <label className="fs-6 fw-semibold mb-2">{t('YouTube')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('YouTube URL')}
                        name="youtube"
                        value={formData.youtube}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="col-md-4 fv-row">
                      <label className="fs-6 fw-semibold mb-2">{t('Facebook')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('Facebook URL')}
                        name="facebook"
                        value={formData.facebook}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="col-md-4 fv-row">
                      <label className="fs-6 fw-semibold mb-2">{t('Threads')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('Threads URL')}
                        name="threads"
                        value={formData.threads}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                  
                  <div className="text-center mt-5">
                    <button type="button" className="btn btn-light me-3" onClick={() => navigate('/admin/agricultural-influencers')}>
                      {t('Discard')}
                    </button>
                    <button type="submit" className="btn btn-base" disabled={isCreating || isUpdating}>
                      <span className="indicator-label">{t('Submit')}</span>
                      {(isCreating || isUpdating) && (
                        <span className="indicator-progress">
                          {t('Please wait...')}
                          <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAgriculturalInfluencerForm;
